import { darken, getContrastRatio } from '@mui/material/styles';
import type { LandingPageConfig } from '@provision/landing-page-utils';
import { isValidColor } from '@provision/membership-plans-ui';
import type { LandingTokens, Surface } from './layouts/types';

const INK_DARKEN = 0.45;

/** Below this a brand button sinks into what sits behind it (WCAG non-text contrast). */
export const MIN_BUTTON_CONTRAST = 3;

/** A colour's contrast against the stop it reads worst on; a gradient is only as good as that. */
export const worstContrast = (color: string, stops: readonly string[]): number =>
  Math.min(...stops.map((stop) => getContrastRatio(color, stop)));

/** Whichever of the layout's light and dark text reads better across every stop. */
export const surfaceFor = (stops: readonly string[], t: LandingTokens): Surface =>
  worstContrast(t.onDark, stops) >= worstContrast(t.ink, stops) ? 'dark' : 'light';

/** A partner-supplied colour, trimmed, or undefined when CSS should never see it. */
export const cleanColor = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed && isValidColor(trimmed) ? trimmed : undefined;
};

/**
 * The three tokens a partner owns; paper, ink and the dark bands stay the layout's. Every tint and
 * gradient derives from these, so an override carries through without a block or style module changing.
 */
export function resolveLandingColors(
  colors: LandingPageConfig['settings']['colors'] | undefined,
): Partial<LandingTokens> | undefined {
  const brand = cleanColor(colors?.primary);
  const secondary = cleanColor(colors?.secondary);
  const accent = cleanColor(colors?.accent);

  if (!brand && !secondary && !accent) return undefined;

  const overrides: Partial<LandingTokens> = {};
  if (brand) overrides.brand = brand;
  if (accent) overrides.gold = accent;

  // A new brand beside the layout's stock ink reads as a mismatched pair, so derive one.
  if (secondary) overrides.brandInk = secondary;
  else if (brand) overrides.brandInk = darken(brand, INK_DARKEN);

  return overrides;
}
